import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CogIcon,
  DesktopComputerIcon,
  LogoutIcon,
  MoonIcon,
  SelectorIcon,
  SunIcon,
  TranslateIcon,
} from '@heroicons/react/outline';
import * as Menu from '@radix-ui/react-dropdown-menu';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';
import { useDocumentDirection } from '@/lib/textDirection';

import { languageOptions } from '@/components/layout/LanguageSelector';
import { SimpleTooltip } from '@/components/ui/tooltip';

import { logout } from '@/utils/auth';
import { useUser } from '@/utils/useUser';

import { ACCOUNT_LINKS, isAccountPath } from './navModel';
import {
  Avatar,
  displayName,
  nav,
  type NavVariant,
  useNavTrack,
} from './navUi';

type ThemePref = 'light' | 'dark' | 'system';

const THEMES: { value: ThemePref; label: string; icon: typeof SunIcon }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'system', label: 'System', icon: DesktopComputerIcon },
];

/**
 * The theme the user picked. `data-theme` on <html> holds the resolved theme
 * after a reload, so the choice is read from localStorage, where the inline
 * script in _document.tsx keeps it. Writing `data-theme` applies it (that
 * script observes the attribute); localStorage is written here too because
 * the attribute may already hold the same value.
 */
function useThemePreference(): [ThemePref, (value: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>('system');
  useEffect(() => {
    try {
      const saved = localStorage.getItem('theme');
      setPref(saved === 'light' || saved === 'dark' ? saved : 'system');
    } catch {
      // Storage blocked: the page follows the system theme.
    }
  }, []);
  const set = (value: ThemePref) => {
    setPref(value);
    try {
      localStorage.setItem('theme', value);
    } catch {
      // Storage blocked: applies to this page only.
    }
    document.documentElement.setAttribute('data-theme', value);
  };
  return [pref, set];
}

/** Switches the page's language the way LanguageSelector does. */
function useSwitchLanguage() {
  const router = useRouter();
  return (value: string) => {
    Cookies.set('NEXT_LOCALE', value, { expires: 365 });
    router.push(router.asPath, router.asPath, { locale: value });
  };
}

function useAccountInfo() {
  const { t } = useLocale('dashboard');
  const { user, userDetails, subscription, isFreePlan } = useUser();
  const name = displayName(userDetails, user?.email) || t('Account');
  const product = subscription?.prices?.products?.name;
  const plan = isFreePlan ? t('Free plan') : product || '';
  return { name, email: user?.email ?? '', plan };
}

const itemClass = clsxm(
  'flex h-9 w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 text-[13px] font-medium outline-none transition-colors',
  nav.body,
  'data-[highlighted]:bg-[#f4f4fb] data-[highlighted]:text-[#10101c] dark:data-[highlighted]:bg-white/[0.07] dark:data-[highlighted]:text-white',
  'data-[state=open]:bg-[#f4f4fb] dark:data-[state=open]:bg-white/[0.07]',
);
const itemIcon =
  'h-[18px] w-[18px] shrink-0 text-[#8b8ba0] dark:text-slate-500';
const menuSurface = clsxm(
  'z-[1100] rounded-2xl p-1.5',
  'bg-white ring-1 ring-[#0f0f28]/[0.08] shadow-[0_1px_2px_rgba(16,16,40,0.06),0_20px_44px_-16px_rgba(49,46,129,0.3)]',
  'dark:bg-slate-800 dark:ring-white/10 dark:shadow-[0_20px_44px_-16px_rgba(0,0,0,0.6)]',
);
const separator = 'mx-1 my-1.5 h-px bg-[#0f0f28]/[0.07] dark:bg-white/[0.08]';

/**
 * The account button at the bottom of the sidebar and the rail: profile,
 * billing, help, theme, language and log out, which used to be four round
 * buttons under the logo plus Logout in the main list.
 */
export function AccountDropdown({ variant }: { variant: 'sidebar' | 'rail' }) {
  const { t, lang } = useLocale('dashboard');
  const { t: tc } = useLocale('common');
  const router = useRouter();
  const track = useNavTrack(variant);
  const dir = useDocumentDirection();
  const { name, email, plan } = useAccountInfo();
  const [theme, setTheme] = useThemePreference();
  const switchLanguage = useSwitchLanguage();
  const here = isAccountPath(router.asPath.split('?')[0]);
  const language =
    languageOptions.find((option) => option.value === lang) ??
    languageOptions[0];

  const trigger =
    variant === 'sidebar' ? (
      <Menu.Trigger
        className={clsxm(
          'group flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2 text-start transition-colors',
          here ? nav.soft : 'hover:bg-[#f4f4fb] dark:hover:bg-white/[0.06]',
          'data-[state=open]:bg-[#f4f4fb] dark:data-[state=open]:bg-white/[0.06]',
          nav.focus,
        )}
      >
        <Avatar size={28} />
        <span className='min-w-0 flex-1'>
          <span
            className={clsxm(
              'block truncate text-[13px] font-semibold leading-4',
              nav.ink,
            )}
          >
            {name}
          </span>
          {/* Always two lines: the plan arrives after the user, and a
              block that grew then would move the name. */}
          <span
            className={clsxm('block truncate text-[12px] leading-4', nav.quiet)}
          >
            {plan || '\u00a0'}
          </span>
        </span>
        <SelectorIcon
          aria-hidden='true'
          className='h-4 w-4 shrink-0 text-[#8b8ba0] dark:text-slate-500'
        />
      </Menu.Trigger>
    ) : (
      <Menu.Trigger
        aria-label={t('Account')}
        className={clsxm(
          'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
          here ? nav.soft : 'hover:bg-[#f4f4fb] dark:hover:bg-white/[0.06]',
          'data-[state=open]:bg-[#f4f4fb] dark:data-[state=open]:bg-white/[0.06]',
          nav.focus,
        )}
      >
        <Avatar size={30} />
      </Menu.Trigger>
    );

  return (
    <Menu.Root dir={dir}>
      {variant === 'rail' ? (
        <SimpleTooltip title={t('Account')} side='right'>
          {trigger}
        </SimpleTooltip>
      ) : (
        trigger
      )}
      <Menu.Portal>
        <Menu.Content
          side={variant === 'rail' ? 'right' : 'top'}
          align={variant === 'rail' ? 'end' : 'start'}
          // In the rail the button is 20 px in from the rail's edge.
          sideOffset={variant === 'rail' ? 28 : 8}
          collisionPadding={8}
          className={clsxm(menuSurface, 'w-64')}
        >
          <div className='px-2.5 pb-2 pt-1.5'>
            <div
              className={clsxm('truncate text-[13px] font-semibold', nav.ink)}
            >
              {name}
            </div>
            {email && (
              <div className={clsxm('truncate text-[12px]', nav.quiet)}>
                {email}
              </div>
            )}
          </div>
          <div className={separator} />
          {ACCOUNT_LINKS.map((group, index) => (
            <div key={index}>
              {index > 0 && <Menu.Separator className={separator} />}
              {group.map((link) => (
                <Menu.Item key={link.href} asChild>
                  <Link
                    href={link.href}
                    onClick={() => track('account', link.href)}
                    className={itemClass}
                  >
                    <link.icon aria-hidden='true' className={itemIcon} />
                    {t(link.name)}
                  </Link>
                </Menu.Item>
              ))}
            </div>
          ))}
          <Menu.Separator className={separator} />
          <Menu.Sub>
            <Menu.SubTrigger className={itemClass}>
              <SunIcon aria-hidden='true' className={itemIcon} />
              <span className='flex-1'>{tc('Theme')}</span>
              <span className={clsxm('text-[12px] font-normal', nav.quiet)}>
                {tc(THEMES.find((option) => option.value === theme)!.label)}
              </span>
              <ChevronRightIcon
                aria-hidden='true'
                className='h-3.5 w-3.5 text-[#8b8ba0] rtl:rotate-180'
              />
            </Menu.SubTrigger>
            <Menu.Portal>
              <Menu.SubContent
                sideOffset={6}
                collisionPadding={8}
                className={clsxm(menuSurface, 'w-44')}
              >
                <Menu.RadioGroup
                  value={theme}
                  onValueChange={(value) => {
                    track('theme', value);
                    setTheme(value as ThemePref);
                  }}
                >
                  {THEMES.map((option) => (
                    <Menu.RadioItem
                      key={option.value}
                      value={option.value}
                      className={itemClass}
                    >
                      <option.icon aria-hidden='true' className={itemIcon} />
                      <span className='flex-1'>{tc(option.label)}</span>
                      <Menu.ItemIndicator>
                        <CheckIcon className='h-4 w-4 text-primary-600 dark:text-primary-300' />
                      </Menu.ItemIndicator>
                    </Menu.RadioItem>
                  ))}
                </Menu.RadioGroup>
              </Menu.SubContent>
            </Menu.Portal>
          </Menu.Sub>
          <Menu.Sub>
            <Menu.SubTrigger className={itemClass}>
              <TranslateIcon aria-hidden='true' className={itemIcon} />
              <span className='flex-1'>{tc('Language')}</span>
              <span className={clsxm('text-[12px] font-normal', nav.quiet)}>
                {language.name}
              </span>
              <ChevronRightIcon
                aria-hidden='true'
                className='h-3.5 w-3.5 text-[#8b8ba0] rtl:rotate-180'
              />
            </Menu.SubTrigger>
            <Menu.Portal>
              <Menu.SubContent
                sideOffset={6}
                collisionPadding={8}
                className={clsxm(
                  menuSurface,
                  'max-h-[min(70vh,30rem)] w-48 overflow-y-auto',
                )}
              >
                <Menu.RadioGroup
                  value={language.value}
                  onValueChange={(value) => {
                    track('language', value);
                    switchLanguage(value);
                  }}
                >
                  {languageOptions.map((option) => (
                    <Menu.RadioItem
                      key={option.value}
                      value={option.value}
                      lang={option.value}
                      className={itemClass}
                    >
                      <span className='flex-1'>{option.name}</span>
                      <Menu.ItemIndicator>
                        <CheckIcon className='h-4 w-4 text-primary-600 dark:text-primary-300' />
                      </Menu.ItemIndicator>
                    </Menu.RadioItem>
                  ))}
                </Menu.RadioGroup>
              </Menu.SubContent>
            </Menu.Portal>
          </Menu.Sub>
          <Menu.Separator className={separator} />
          <Menu.Item
            className={itemClass}
            onSelect={() => {
              track('account', 'logout');
              logout();
            }}
          >
            <LogoutIcon
              aria-hidden='true'
              className={clsxm(itemIcon, 'rtl:rotate-180')}
            />
            {t('Logout')}
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** One-tap Settings next to the account button (3,100 desktop users a month). */
export function SettingsLink({ variant }: { variant: NavVariant }) {
  const { t } = useLocale('dashboard');
  const { asPath } = useRouter();
  const track = useNavTrack(variant);
  const active = asPath.split('?')[0] === '/settings';
  return (
    <SimpleTooltip title={t('Settings')} side='top'>
      <Link
        href='/settings'
        aria-label={t('Settings')}
        aria-current={active ? 'page' : undefined}
        onClick={() => track('account', '/settings', { shortcut: true })}
        className={clsxm(
          'flex shrink-0 items-center justify-center rounded-xl transition-colors',
          variant === 'drawer' ? 'h-11 w-11' : 'h-9 w-9',
          active ? nav.active : clsxm(nav.quiet, nav.hover),
          nav.focus,
        )}
      >
        <CogIcon aria-hidden='true' className='h-[18px] w-[18px]' />
      </Link>
    </SimpleTooltip>
  );
}

/**
 * The phone drawer's account section: the same links as the dropdown, open
 * in place (a menu over a drawer fights the drawer's focus trap), with the
 * native language picker phones are good at.
 */
export function AccountPanel({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, lang } = useLocale('dashboard');
  const { t: tc } = useLocale('common');
  const router = useRouter();
  const track = useNavTrack('drawer');
  const { name, email, plan } = useAccountInfo();
  const [theme, setTheme] = useThemePreference();
  const switchLanguage = useSwitchLanguage();
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const here = isAccountPath(router.asPath.split('?')[0]);

  // Bring the opened section into view; it opens below the button.
  useEffect(() => {
    if (open) panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  const row = clsxm(
    'flex h-11 w-full items-center gap-3 rounded-xl px-3 text-[15px] font-medium transition-colors',
    nav.body,
    nav.hover,
    nav.focus,
  );

  return (
    <div>
      <div className='flex items-center gap-1.5'>
        <button
          type='button'
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            track('account', open ? 'collapse' : 'expand');
            onOpenChange(!open);
          }}
          className={clsxm(
            'flex h-12 min-w-0 flex-1 items-center gap-3 rounded-xl px-2 text-start transition-colors',
            here || open
              ? nav.soft
              : 'hover:bg-[#f4f4fb] dark:hover:bg-white/[0.06]',
            nav.focus,
          )}
        >
          <Avatar size={32} />
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm(
                'block truncate text-[15px] font-semibold leading-5',
                nav.ink,
              )}
            >
              {name}
            </span>
            <span
              className={clsxm(
                'block truncate text-[13px] leading-4',
                nav.quiet,
              )}
            >
              {plan || '\u00a0'}
            </span>
          </span>
          <ChevronDownIcon
            aria-hidden='true'
            className={clsxm(
              'h-4 w-4 shrink-0 text-[#8b8ba0] transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
        <SettingsLink variant='drawer' />
      </div>
      <div ref={panelRef} id={panelId} hidden={!open} className='pt-1'>
        {email && (
          <div className={clsxm('truncate px-3 pb-1 text-[13px]', nav.quiet)}>
            {email}
          </div>
        )}
        {ACCOUNT_LINKS.flat().map((link) => (
          <Link
            key={link.href}
            href={link.href}
            onClick={() => track('account', link.href)}
            className={row}
          >
            <link.icon aria-hidden='true' className={itemIcon} />
            {t(link.name)}
          </Link>
        ))}
        <PanelRow icon={<SunIcon aria-hidden='true' className={itemIcon} />}>
          <span className='flex-1'>{tc('Theme')}</span>
          <div
            role='radiogroup'
            aria-label={tc('Theme')}
            className={clsxm('flex rounded-lg p-0.5', nav.soft)}
          >
            {THEMES.map((option) => (
              <button
                key={option.value}
                type='button'
                role='radio'
                aria-checked={theme === option.value}
                aria-label={tc(option.label)}
                title={tc(option.label)}
                onClick={() => {
                  track('theme', option.value);
                  setTheme(option.value);
                }}
                className={clsxm(
                  'flex h-8 w-9 items-center justify-center rounded-md transition',
                  theme === option.value
                    ? 'bg-white text-primary-600 shadow-sm ring-1 ring-[#0f0f28]/[0.06] dark:bg-slate-700 dark:text-primary-300 dark:ring-white/10'
                    : nav.quiet,
                  nav.focus,
                )}
              >
                <option.icon aria-hidden='true' className='h-4 w-4' />
              </button>
            ))}
          </div>
        </PanelRow>
        <PanelRow
          icon={<TranslateIcon aria-hidden='true' className={itemIcon} />}
        >
          <label htmlFor={`${panelId}-language`} className='flex-1'>
            {tc('Language')}
          </label>
          <select
            id={`${panelId}-language`}
            value={lang}
            onChange={(event) => {
              track('language', event.target.value);
              switchLanguage(event.target.value);
            }}
            className={clsxm(
              'h-9 rounded-lg border-0 py-0 pe-8 ps-3 text-[14px] font-medium ring-1 ring-[#0f0f28]/[0.08] focus:ring-2 focus:ring-primary-600 dark:bg-slate-800 dark:ring-white/10',
              nav.ink,
            )}
          >
            {languageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
        </PanelRow>
        <button
          type='button'
          onClick={() => {
            track('account', 'logout');
            logout();
          }}
          className={row}
        >
          <LogoutIcon
            aria-hidden='true'
            className={clsxm(itemIcon, 'rtl:rotate-180')}
          />
          {t('Logout')}
        </button>
      </div>
    </div>
  );
}

function PanelRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      className={clsxm(
        'flex h-12 items-center gap-3 px-3 text-[15px] font-medium',
        nav.body,
      )}
    >
      {icon}
      {children}
    </div>
  );
}
